import openai from './openaiClient';
import {
  APIConnectionError,
  AuthenticationError,
  PermissionDeniedError,
  RateLimitError,
  InternalServerError
} from 'openai';

/**
 * Maps OpenAI API error types to user-friendly error messages.
 * @param {Error} error - The error object from OpenAI API.
 * @returns {Object} Error information with isInternal flag and message.
 */
function getErrorMessage(error) {
  if (error instanceof AuthenticationError) {
    return { isInternal: true, message: 'Invalid API key or authentication failed. Please check your OpenAI API key.' };
  } else if (error instanceof PermissionDeniedError) {
    return { isInternal: true, message: 'Quota exceeded or authorization failed. You may have exceeded your usage limits or do not have access to this resource.' };
  } else if (error instanceof RateLimitError) {
    return { isInternal: true, message: 'Rate limit exceeded. You are sending requests too quickly. Please wait a moment and try again.' };
  } else if (error instanceof InternalServerError) {
    return { isInternal: true, message: 'OpenAI service is currently unavailable. Please try again later.' };
  } else if (error instanceof APIConnectionError) {
    return { isInternal: true, message: 'Unable to connect to OpenAI service. Please check your API key and internet connection.' };
  } else {
    return { isInternal: false, message: error?.message || 'An unexpected error occurred. Please try again.' };
  }
}

export const openaiService = {
  /**
   * Generates AI-powered analytics insights for gym operations.
   * @param {Object} analyticsData - Current analytics data (KPIs, trends, member data).
   * @returns {Promise<Object>} AI-generated insights with recommendations.
   */
  async generateAnalyticsInsights(analyticsData) {
    try {
      const response = await openai?.chat?.completions?.create({
        model: 'gpt-5-mini',
        messages: [
          {
            role: 'system',
            content: 'You are an expert gym business analyst. Analyze the provided data and generate actionable insights for gym operations, member retention, and revenue optimization. Focus on practical, data-driven recommendations.'
          },
          {
            role: 'user',
            content: `Analyze this gym analytics data and provide insights: ${JSON.stringify(analyticsData)}`
          }
        ],
        response_format: {
          type: 'json_schema',
          json_schema: {
            name: 'analytics_insights',
            schema: {
              type: 'object',
              properties: {
                insights: {
                  type: 'array',
                  items: {
                    type: 'object',
                    properties: {
                      category: { type: 'string', enum: ['optimization', 'expansion', 'risk', 'opportunity'] },
                      priority: { type: 'string', enum: ['high', 'medium', 'low'] },
                      title: { type: 'string' },
                      description: { type: 'string' },
                      recommendations: { type: 'array', items: { type: 'string' } },
                      potentialImpact: { type: 'string' },
                      implementationTime: { type: 'string' }
                    },
                    required: ['category', 'priority', 'title', 'description', 'recommendations']
                  }
                },
                summary: { type: 'string' }
              },
              required: ['insights', 'summary'],
              additionalProperties: false
            }
          }
        },
        reasoning_effort: 'medium',
        verbosity: 'medium'
      });

      return JSON.parse(response?.choices?.[0]?.message?.content);
    } catch (error) {
      const errorInfo = getErrorMessage(error);
      if (errorInfo?.isInternal) {
        console.log(errorInfo?.message);
      } else {
        console.error('Error generating analytics insights:', error);
      }
      throw new Error(errorInfo.message);
    }
  },

  /**
   * Analyzes member retention data and generates AI-powered retention strategies.
   * @param {Object} retentionData - Member engagement and churn data.
   * @returns {Promise<Object>} Retention strategies and at-risk member analysis.
   */
  async generateRetentionStrategies(retentionData) {
    try {
      const response = await openai?.chat?.completions?.create({
        model: 'gpt-5-mini',
        messages: [
          {
            role: 'system',
            content: 'You are a member retention specialist for fitness facilities. Analyze member engagement patterns, identify churn risks, and recommend personalized retention strategies. Focus on actionable interventions that improve member satisfaction and reduce churn.'
          },
          {
            role: 'user',
            content: `Analyze this member retention data and provide strategies: ${JSON.stringify(retentionData)}`
          }
        ],
        response_format: {
          type: 'json_schema',
          json_schema: {
            name: 'retention_strategies',
            schema: {
              type: 'object',
              properties: {
                atRiskMembers: {
                  type: 'array',
                  items: {
                    type: 'object',
                    properties: {
                      memberId: { type: 'string' },
                      name: { type: 'string' },
                      riskScore: { type: 'number' },
                      riskFactors: { type: 'array', items: { type: 'string' } },
                      recommendedActions: { type: 'array', items: { type: 'string' } }
                    },
                    required: ['name', 'riskScore', 'riskFactors', 'recommendedActions']
                  }
                },
                retentionStrategies: {
                  type: 'array',
                  items: {
                    type: 'object',
                    properties: {
                      strategy: { type: 'string' },
                      targetSegment: { type: 'string' },
                      expectedImpact: { type: 'string' },
                      implementation: { type: 'string' },
                      priority: { type: 'string', enum: ['high', 'medium', 'low'] }
                    },
                    required: ['strategy', 'targetSegment', 'expectedImpact', 'priority']
                  }
                },
                engagementMetrics: {
                  type: 'object',
                  properties: {
                    overallHealth: { type: 'string' },
                    keyFindings: { type: 'array', items: { type: 'string' } },
                    recommendations: { type: 'array', items: { type: 'string' } }
                  },
                  required: ['overallHealth', 'keyFindings', 'recommendations']
                }
              },
              required: ['atRiskMembers', 'retentionStrategies', 'engagementMetrics'],
              additionalProperties: false
            }
          }
        },
        reasoning_effort: 'medium',
        verbosity: 'medium'
      });

      return JSON.parse(response?.choices?.[0]?.message?.content);
    } catch (error) {
      const errorInfo = getErrorMessage(error);
      if (errorInfo?.isInternal) {
        console.log(errorInfo?.message);
      } else {
        console.error('Error generating retention strategies:', error);
      }
      throw new Error(errorInfo.message);
    }
  },

  /**
   * Generates personalized member engagement recommendations.
   * @param {Object} memberData - Individual member activity and preferences.
   * @returns {Promise<Object>} Personalized engagement recommendations.
   */
  async generateMemberEngagementPlan(memberData) {
    try {
      const response = await openai?.chat?.completions?.create({
        model: 'gpt-5-mini',
        messages: [
          {
            role: 'system',
            content: 'You are a fitness engagement specialist. Create personalized engagement plans based on member activity patterns, preferences, and goals. Provide specific, actionable recommendations to increase member satisfaction and gym usage.'
          },
          {
            role: 'user',
            content: `Create an engagement plan for this member: ${JSON.stringify(memberData)}`
          }
        ],
        response_format: {
          type: 'json_schema',
          json_schema: {
            name: 'engagement_plan',
            schema: {
              type: 'object',
              properties: {
                recommendations: {
                  type: 'array',
                  items: {
                    type: 'object',
                    properties: {
                      action: { type: 'string' },
                      reason: { type: 'string' },
                      expectedOutcome: { type: 'string' },
                      priority: { type: 'string', enum: ['high', 'medium', 'low'] }
                    },
                    required: ['action', 'reason', 'priority']
                  }
                },
                engagementScore: { type: 'number' },
                summary: { type: 'string' }
              },
              required: ['recommendations', 'engagementScore', 'summary'],
              additionalProperties: false
            }
          }
        },
        reasoning_effort: 'minimal',
        verbosity: 'low'
      });

      return JSON.parse(response?.choices?.[0]?.message?.content);
    } catch (error) {
      const errorInfo = getErrorMessage(error);
      if (errorInfo?.isInternal) {
        console.log(errorInfo?.message);
      } else {
        console.error('Error generating engagement plan:', error);
      }
      throw new Error(errorInfo.message);
    }
  }
};